import React, { useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { toast } from "../../../components/toast";
import LabDashboardLayout from "../../../components/labmod-dashboard-layout/LabModDashboardLayout";
import styles from "../../../styles/__setting.module.scss";
import { APP_PATH } from "../../../api/backendPath";

const ChangePassword = ({ onLogout, onNavigateToDashboard }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Password validation rules
  const passwordRules = [
    { label: "At least 8 characters", test: (p) => p.length >= 8 },
    { label: "At least one lowercase letter", test: (p) => /[a-z]/.test(p) },
    { label: "At least one uppercase letter", test: (p) => /[A-Z]/.test(p) },
    { label: "At least one number", test: (p) => /[0-9]/.test(p) },
    {
      label: "At least one special character",
      test: (p) => /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(p),
    },
  ];

  const isPasswordValid =
    newPassword.length > 0 &&
    passwordRules.every((rule) => rule.test(newPassword));

  const handleNavigate = (page) => {
    if (page === "dashboard") onNavigateToDashboard?.();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};

    if (!currentPassword.trim()) {
      newErrors.currentPassword = "Current password is required.";
    }
    if (!newPassword.trim()) {
      newErrors.newPassword = "New password is required.";
    } else if (!isPasswordValid) {
      newErrors.newPassword = "Password does not meet all requirements.";
    }
    if (!confirmPassword.trim()) {
      newErrors.confirmPassword = "Please confirm your new password.";
    } else if (newPassword !== confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match.";
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    setIsLoading(true);
    try {
      const token = sessionStorage.getItem("usr_1a2b3c");
      const response = await axios.post(
        process.env.REACT_APP_API_URL + APP_PATH.changePassword,
        {
          current_password: currentPassword,
          new_password: newPassword,
          confirm_password: confirmPassword,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.data.status === true) {
        toast.success("Password changed successfully!");
        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");
        setTimeout(() => navigate("/lab_setting"), 1500);
      } else {
        toast.error(response.data.message || "Failed to change password");
      }
    } catch (error) {
      const msg =
        error?.response?.data?.message ||
        "Failed to change password. Please try again.";
      toast.error(msg);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <LabDashboardLayout
      currentPage="lab_setting"
      onNavigate={handleNavigate}
      onLogout={onLogout}
    >
      <div className={styles["settings-container"]}>
        <div className="">
          <div className="row justify-content-center">
            <div className="col-12 col-lg-12">
              <h1 className={styles["settings-title"]}>Change Password</h1>

              <div className={styles["settings-card"]}>
                <form
                  onSubmit={handleSubmit}
                  style={{ maxWidth: "400px", padding: "20px" }}
                >
                  {/* Current Password */}
                  <div style={{ marginBottom: "16px" }}>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "6px",
                        fontSize: "14px",
                        fontWeight: "500",
                        color: "#333",
                      }}
                    >
                      Current Password{" "}
                      <span style={{ color: "#ef4444" }}>*</span>
                    </label>
                    <div style={{ position: "relative" }}>
                      <input
                        type={showCurrent ? "text" : "password"}
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        placeholder="Enter current password"
                        style={{
                          width: "100%",
                          padding: "10px 40px 10px 12px",
                          border: errors.currentPassword
                            ? "1px solid #ef4444"
                            : "1px solid #d1d5db",
                          borderRadius: "8px",
                          fontSize: "14px",
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => setShowCurrent(!showCurrent)}
                        style={{
                          position: "absolute",
                          right: "10px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                        }}
                      >
                        {showCurrent ? (
                          <EyeOff size={18} color="#666" />
                        ) : (
                          <Eye size={18} color="#666" />
                        )}
                      </button>
                    </div>
                    {errors.currentPassword && (
                      <p
                        style={{
                          color: "#ef4444",
                          fontSize: "12px",
                          marginTop: "4px",
                        }}
                      >
                        {errors.currentPassword}
                      </p>
                    )}
                  </div>

                  {/* New Password */}
                  <div style={{ marginBottom: "16px" }}>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "6px",
                        fontSize: "14px",
                        fontWeight: "500",
                        color: "#333",
                      }}
                    >
                      New Password <span style={{ color: "#ef4444" }}>*</span>
                    </label>
                    <div style={{ position: "relative" }}>
                      <input
                        type={showNew ? "text" : "password"}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="Enter new password"
                        style={{
                          width: "100%",
                          padding: "10px 40px 10px 12px",
                          border: errors.newPassword
                            ? "1px solid #ef4444"
                            : "1px solid #d1d5db",
                          borderRadius: "8px",
                          fontSize: "14px",
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => setShowNew(!showNew)}
                        style={{
                          position: "absolute",
                          right: "10px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                        }}
                      >
                        {showNew ? (
                          <EyeOff size={18} color="#666" />
                        ) : (
                          <Eye size={18} color="#666" />
                        )}
                      </button>
                    </div>
                    {errors.newPassword && (
                      <p
                        style={{
                          color: "#ef4444",
                          fontSize: "12px",
                          marginTop: "4px",
                        }}
                      >
                        {errors.newPassword}
                      </p>
                    )}

                    {/* Password Rules Checklist */}
                    {newPassword.length > 0 && (
                      <div style={{ marginTop: "10px" }}>
                        {passwordRules.map((rule, idx) => {
                          const passed = rule.test(newPassword);
                          return (
                            <div
                              key={idx}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "6px",
                                marginBottom: "4px",
                              }}
                            >
                              <span
                                style={{
                                  color: passed ? "#10b981" : "#ef4444",
                                  fontSize: "13px",
                                }}
                              >
                                {passed ? "✓" : "✖"}
                              </span>
                              <span
                                style={{
                                  fontSize: "12px",
                                  color: passed ? "#10b981" : "#6b7280",
                                }}
                              >
                                {rule.label}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Confirm Password */}
                  <div style={{ marginBottom: "24px" }}>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "6px",
                        fontSize: "14px",
                        fontWeight: "500",
                        color: "#333",
                      }}
                    >
                      Confirm New Password{" "}
                      <span style={{ color: "#ef4444" }}>*</span>
                    </label>
                    <div style={{ position: "relative" }}>
                      <input
                        type={showConfirm ? "text" : "password"}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="Confirm new password"
                        style={{
                          width: "100%",
                          padding: "10px 40px 10px 12px",
                          border: errors.confirmPassword
                            ? "1px solid #ef4444"
                            : "1px solid #d1d5db",
                          borderRadius: "8px",
                          fontSize: "14px",
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirm(!showConfirm)}
                        style={{
                          position: "absolute",
                          right: "10px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                        }}
                      >
                        {showConfirm ? (
                          <EyeOff size={18} color="#666" />
                        ) : (
                          <Eye size={18} color="#666" />
                        )}
                      </button>
                    </div>
                    {errors.confirmPassword && (
                      <p
                        style={{
                          color: "#ef4444",
                          fontSize: "12px",
                          marginTop: "4px",
                        }}
                      >
                        {errors.confirmPassword}
                      </p>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading || !isPasswordValid}
                    style={{
                      width: "100%",
                      padding: "12px",
                      backgroundColor: isPasswordValid ? "#0DD0D0" : "#d1d5db",
                      color: isPasswordValid ? "#000" : "#666",
                      border: "none",
                      borderRadius: "8px",
                      fontSize: "14px",
                      fontWeight: "600",
                      cursor:
                        isLoading || !isPasswordValid
                          ? "not-allowed"
                          : "pointer",
                      opacity: isLoading ? 0.7 : 1,
                    }}
                  >
                    {isLoading ? "Changing..." : "Change Password"}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </LabDashboardLayout>
  );
};

export default ChangePassword;
